import React, { useState, useCallback, useContext } from 'react';
import Model from '../common/models/Model';
import LinkDetailsCard from '../common/cards/LinkDetails';
import LinksList from '../dashboard/LinkList';
import AnalyticsSection from '../dashboard/AnalyticsSection';
import TabNavigation from '../dashboard/TabNavigation';
import { ILink } from '../../interfaces/Link';
import LinkShareCard from '../LinkShareCard/LinkShare';
import LinkAndAppearanceEditCard from '../common/cards/EditLinkandAppearance';
import useDeviceDetect from '../../helpers/screens';
import { UserContext } from '../../context/UserContext';
import useI18n from '../../hooks/useI18n';
import useSubscription from '../../hooks/useSubscription';
import { generateSmartLink } from '@/utils/smartLinkUtils';

interface DefaultDashboardProps {
  links: ILink[];
  tabs: { label: string; value: string }[];
  selectedTab: number;
  setSelectedTab: (val: number) => void;
  refetchLinks: () => Promise<void>;
}

type ModalType = 'edit' | 'details' | 'share' | null;

const DefaultDashboard: React.FC<DefaultDashboardProps> = ({
  links,
  tabs,
  selectedTab,
  setSelectedTab,
  refetchLinks,
}) => {
  const { t } = useI18n();
  const { isMobile } = useDeviceDetect();
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;

  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [selectedLink, setSelectedLink] = useState<ILink | null>(null);

  const { hasSubscription } = useSubscription();
  const isConnectedWithYoutube = !!(user?.youtube && user?.youtube.accessToken);

  const openModal = useCallback(
    (type: ModalType, id: string) => {
      const link = links.find((link) => link._id === id);
      if (link) {
        setSelectedLink(link);
        setModalType(type);
      }
    },
    [links],
  );

  const closeModal = useCallback(() => {
    setModalType(null);
    setSelectedLink(null);
  }, []);

  return (
    <div>
      {modalType === 'edit' && selectedLink && (
        <Model
          isOpen
          onClose={closeModal}
          direction={isMobile ? 'bottom' : 'center'}
        >
          <LinkAndAppearanceEditCard
            link={selectedLink}
            handleModalClose={closeModal}
            hasSubscription={hasSubscription || false}
            refetchLinks={refetchLinks}
          />
        </Model>
      )}
      {modalType === 'details' && selectedLink && (
        <Model
          isOpen
          onClose={closeModal}
          direction={isMobile ? 'bottom' : 'center'}
        >
          <LinkDetailsCard
            data={selectedLink}
            handleDetailsModalClose={closeModal}
            hasSubscription={hasSubscription || false}
            isConnectedWithYoutube={isConnectedWithYoutube}
          />
        </Model>
      )}
      {modalType === 'share' && selectedLink && (
        <Model isOpen onClose={closeModal}>
          <LinkShareCard
            handleShareModalClose={closeModal}
            link={generateSmartLink(selectedLink)}
          />
        </Model>
      )}

      <div className="bg-white p-[12px] sm:p-[24px]">
        <div className="flex flex-row justify-between items-center">
          <h1 className="font-semibold text-lg font-content">
            {t('dashboard.myLinks')}
          </h1>
          <TabNavigation
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
        </div>
        <LinksList
          links={links}
          editModalOpen={(id) => openModal('edit', id)}
          detailsModalOpen={(id) => openModal('details', id)}
          shareModalOpen={(id) => openModal('share', id)}
        />
        <AnalyticsSection hasSubscription={hasSubscription || false} />
      </div>
    </div>
  );
};
export default DefaultDashboard;
